"use client";

import { useState } from "react";

import { revalidateLogic } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { z } from "zod";

import { Card, CardContent, CardHeader, CardTitle } from "@louez/ui";
import { CalendarCheckIcon } from "@louez/ui/icons";

import { RootError } from "@/components/form/root-error";
import { useAppForm } from "@/hooks/form/form";
import { orpcClient } from "@/lib/orpc/react";

const bookingFormSchema = z.object({
  customerName: z.string().trim().min(2).max(255),
  customerEmail: z.email().max(255),
  customerPhone: z.string().trim().max(50),
  scheduledStart: z.string().min(1),
  customerNotes: z.string().trim().max(2000),
});

type ServiceBookingFormProps = {
  serviceId: string;
};

export const ServiceBookingForm = ({
  serviceId,
}: ServiceBookingFormProps) => {
  const t = useTranslations("storefront.services");
  const [bookingId, setBookingId] = useState<string | null>(null);
  const bookingMutation = useMutation({
    mutationFn: (values: z.infer<typeof bookingFormSchema>) =>
      orpcClient.storefront.services.book({
        serviceId,
        customerName: values.customerName,
        customerEmail: values.customerEmail,
        customerPhone: values.customerPhone || null,
        scheduledStart: new Date(values.scheduledStart).toISOString(),
        customerNotes: values.customerNotes || null,
      }),
  });
  const form = useAppForm({
    defaultValues: {
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      scheduledStart: "",
      customerNotes: "",
    },
    validationLogic: revalidateLogic({
      mode: "submit",
      modeAfterSubmission: "change",
    }),
    validators: { onSubmit: bookingFormSchema },
    onSubmit: async ({ value }) => {
      const result = await bookingMutation
        .mutateAsync(value)
        .catch(() => null);
      if (result) setBookingId(result.id);
    },
  });

  if (bookingId) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center py-12 text-center">
          <span className="bg-primary/10 text-primary mb-4 flex size-14 items-center justify-center rounded-full">
            <CalendarCheckIcon className="size-7" />
          </span>
          <h2 className="text-xl font-semibold">{t("appointmentSent")}</h2>
          <p className="text-muted-foreground mt-2 max-w-md text-sm">
            {t("appointmentSentDescription")}
          </p>
          <p className="text-muted-foreground mt-4 text-xs">
            {t("reference", { id: bookingId })}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("requestAppointment")}</CardTitle>
      </CardHeader>
      <CardContent>
        <form.AppForm>
          <form.Form className="space-y-5" formName="service-booking">
            <RootError error={bookingMutation.error?.message ?? null} />

            <form.AppField name="customerName">
              {(field) => <field.Input label={t("name")} />}
            </form.AppField>
            <form.AppField name="customerEmail">
              {(field) => (
                <field.Input label={t("email")} type="email" />
              )}
            </form.AppField>
            <form.AppField name="customerPhone">
              {(field) => <field.Input label={t("phone")} type="tel" />}
            </form.AppField>
            <form.AppField name="scheduledStart">
              {(field) => (
                <field.Input
                  label={t("dateTime")}
                  type="datetime-local"
                  min={new Date(Date.now() + 60_000)
                    .toISOString()
                    .slice(0, 16)}
                />
              )}
            </form.AppField>
            <form.AppField name="customerNotes">
              {(field) => (
                <field.Textarea
                  label={t("notes")}
                  rows={4}
                />
              )}
            </form.AppField>
            <form.SubscribeButton className="w-full">
              {t("sendRequest")}
            </form.SubscribeButton>
          </form.Form>
        </form.AppForm>
      </CardContent>
    </Card>
  );
};
